/* ═══════════════════════════════════════════════════
   TrackLab v2 — View Switching & DOM Mutations
   ═══════════════════════════════════════════════════ */

(function () {
  'use strict';

  TL.views = {};

  // Default silk-fallback background when no silk_color is available on the
  // parent runner element. Matches the .v2-page .r-num default in v2.css.
  var SILK_FALLBACK_BG = 'var(--accent-blue, #4a90e2)';

  /**
   * Apply a fallback to an <img> whose silk failed to load.
   * Removes the broken image, paints the parent silk container with the
   * runner's silk_color (if present, else a neutral default), and surfaces
   * the cloth number in the empty cell so the runner is still identifiable.
   *
   * Reads `data-cloth` and `data-silk-color` from the closest ancestor
   * carrying them — runner cards already expose both on the `.runner` row.
   *
   * Idempotent: a second error event on the same img is a no-op because the
   * img is removed before the handler returns.
   */
  function _silkFallback(img) {
    if (!img || !img.parentNode) return;
    var parent = img.parentNode;
    var ownerCloth = img.closest('[data-cloth]');
    var ownerColor = img.closest('[data-silk-color]');
    var cloth = (img.dataset.cloth ||
                 (ownerCloth && ownerCloth.dataset.cloth) ||
                 '').trim();
    var color = (img.dataset.silkColor ||
                 (ownerColor && ownerColor.dataset.silkColor) ||
                 '').trim();

    parent.classList.add('silk-fallback');
    parent.style.background = color || SILK_FALLBACK_BG;
    img.remove();
    if (cloth && !parent.textContent.trim()) {
      parent.textContent = cloth;
    }
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[TrackLab] silk image failed to load', img.src || '(unset)');
    }
  }

  // Exposed for tests and for external callers (e.g. results.js).
  TL.views.applySilkFallback = _silkFallback;

  function _activateSilks(container) {
    container.querySelectorAll('img[data-src]').forEach(function (img) {
      // Bind the fallback BEFORE assigning src so a synchronous error
      // (cached 404) still routes through the handler.
      img.addEventListener('error', function onSilkError() {
        img.removeEventListener('error', onSilkError);
        _silkFallback(img);
      });
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    });
  }

  // ── URL hash helpers for back-button restore ──

  function _slugify(str) {
    return (str || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }

  function _buildHash(el) {
    var venue = el.dataset.venue || '';
    var time = el.dataset.time || '';
    if (!venue || !time) return '';
    return _slugify(venue) + '-' + time;
  }

  function _setHash(el) {
    var hash = _buildHash(el);
    if (hash) history.replaceState(null, '', '#' + hash);
  }

  function _clearHash() {
    history.replaceState(null, '', window.location.pathname + window.location.search);
  }

  /**
   * On page load, read the URL hash and expand the matching race.
   */
  TL.views.restoreFromHash = function () {
    var hash = window.location.hash.replace('#', '');
    if (!hash) return;

    // Collapse any currently expanded cards first
    _collapseAll();
    document.querySelectorAll('.result-card.expanded').forEach(function (c) {
      c.classList.remove('expanded');
    });

    // Search race rows, live banners, and result cards
    var candidates = document.querySelectorAll('.race-row[data-venue], .live-banner[data-venue], .result-card[data-venue]');
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (_buildHash(el) === hash) {
        if (el.classList.contains('live-banner')) {
          TL.views.toggleLiveBanner(el.dataset.raceId);
        } else if (el.classList.contains('race-row')) {
          TL.views.toggleUpcomingRace(el.dataset.raceId);
        } else if (el.classList.contains('result-card')) {
          TL.results.toggleResult(el);
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        return;
      }
    }
  };

  TL.views.showRace = function (raceId) {
    // Hide all list-level content
    var listView = document.getElementById('v2ListView');
    if (listView) listView.classList.add('hidden');
    document.querySelectorAll(
      '.v2-page .v2-meeting-group, .v2-page .v2-venue-filters, ' +
      '.v2-page .v2-day-header, .v2-page .section-label, .v2-page .section-subtitle'
    ).forEach(function (el) {
      el.classList.add('hidden');
    });

    var exp = document.getElementById('v2Expanded-' + raceId);
    if (!exp) return;
    exp.classList.add('active');
    _activateSilks(exp);
    TL.state.activeRaceId = raceId;
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Auto-select first runner if none selected
    var firstRunner = exp.querySelector('.runner');
    if (firstRunner && !exp.querySelector('.runner.sel')) {
      TL.runner.selectRunner(firstRunner, raceId);
    }

    TL.polling.start(raceId, exp.dataset.eventId);
    TL.progress.update(raceId);
  };

  TL.views.showList = function () {
    if (TL.state.activeRaceId) {
      var exp = document.getElementById('v2Expanded-' + TL.state.activeRaceId);
      if (exp) exp.classList.remove('active');
    }
    // Restore all list-level content
    var listView = document.getElementById('v2ListView');
    if (listView) listView.classList.remove('hidden');
    document.querySelectorAll(
      '.v2-page .v2-meeting-group, .v2-page .v2-venue-filters, ' +
      '.v2-page .v2-day-header, .v2-page .section-label, .v2-page .section-subtitle'
    ).forEach(function (el) {
      el.classList.remove('hidden');
    });
    TL.state.activeRaceId = null;
    TL.polling.stopAll();
  };

  TL.views.toggleOverflow = function (raceId, totalRunners) {
    var overflow = document.getElementById('v2Overflow-' + raceId);
    var btn = overflow ? overflow.nextElementSibling : null;
    if (!overflow) return;

    if (overflow.classList.contains('open')) {
      overflow.classList.remove('open');
      if (btn) btn.innerHTML = 'Show all ' + totalRunners + ' runners &#x2193;';
    } else {
      overflow.classList.add('open');
      if (btn) btn.innerHTML = 'Show top 5 only &#x2191;';
    }
  };

  /**
   * Collapse all open inline details and expanded race rows/banners.
   */
  // Overlay element for dimming the screen behind expanded race
  var _overlay = null;
  function _getOverlay() {
    if (!_overlay) {
      _overlay = document.createElement('div');
      _overlay.className = 'race-detail-overlay';
      _overlay.addEventListener('click', function () {
        _collapseAll();
        // Also collapse any expanded result cards
        document.querySelectorAll('.v2-page .result-card.expanded').forEach(function (c) {
          c.classList.remove('expanded');
        });
        _clearHash();
      });
      document.querySelector('.v2-page').appendChild(_overlay);
    }
    return _overlay;
  }

  function _showOverlay() { _getOverlay().classList.add('active'); }
  function _hideOverlay() { if (_overlay) _overlay.classList.remove('active'); }

  // Expose overlay controls for other modules (e.g. results)
  TL.views.showOverlay = _showOverlay;
  TL.views.hideOverlay = _hideOverlay;

  function _collapseAll() {
    // Stop the live-score poller of a previously expanded upcoming card
    if (TL.state.upcomingPollId && TL.polling) {
      TL.polling.stop(TL.state.upcomingPollId);
      TL.state.upcomingPollId = null;
    }
    // Single detail toggle goes back to its page-level slot
    if (TL.detail && TL.detail.unmount) TL.detail.unmount();
    document.querySelectorAll('.race-inline-detail.open').forEach(function (d) {
      d.classList.remove('open');
      if (d.previousElementSibling) d.previousElementSibling.classList.remove('expanded');
    });
    // Also collapse all live banners
    document.querySelectorAll('.live-banner.expanded').forEach(function (b) {
      b.classList.remove('expanded');
    });
    _hideOverlay();
    // Stop track animation when collapsing
    if (TL.track) TL.track.stop();
  }

  /**
   * Toggle the live banner's inline detail section.
   */
  TL.views.toggleLiveBanner = function (raceId) {
    var banner = document.querySelector('.live-banner[data-race-id="' + raceId + '"]');
    var detail = document.getElementById('v2InlineDetail-' + raceId);
    if (!banner || !detail) return;

    // Mark that user has interacted — suppress auto-expand from schedule poll
    if (TL.schedule) TL.schedule.userInteracted();

    var isOpen = detail.classList.contains('open');

    // Collapse everything first
    _collapseAll();

    if (!isOpen) {
      detail.classList.add('open');
      _activateSilks(detail);
      banner.classList.add('expanded');
      setTimeout(function () {
        banner.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 380);
      _setHash(banner);
      if (TL.analytics) TL.analytics.trackRaceExpand(raceId, 'live');
      _showOverlay();

      // Start track animation after panel transition so Lottie can measure its container
      setTimeout(function () {
        if (TL.track) TL.track.start();
        // Re-apply silk from selected runner (stop() cleared the colour state)
        var sel = detail.querySelector('.runner.sel');
        if (sel && TL.runner) {
          TL.runner.selectRunner(sel, raceId);
        }
      }, 400);

      // Auto-select first runner if none selected
      var firstRunner = detail.querySelector('.runner');
      if (firstRunner && !detail.querySelector('.runner.sel')) {
        TL.runner.selectRunner(firstRunner, raceId);
      }
    } else {
      _clearHash();
    }
  };

  /**
   * Toggle inline race detail on the upcoming page.
   */
  TL.views.toggleUpcomingRace = function (raceId) {
    var detail = document.getElementById('v2InlineDetail-' + raceId);
    if (!detail) return;

    var row = detail.previousElementSibling;
    var isOpen = detail.classList.contains('open');

    _collapseAll();

    if (!isOpen) {
      detail.classList.add('open');
      if (TL.detail && TL.detail.mount) TL.detail.mount(detail.querySelector('.race-runners'));
      _activateSilks(detail);
      TL.state.activeRaceId = raceId;
      if (row) {
        row.classList.add('expanded');
        setTimeout(function () {
          row.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 380);
        _setHash(row);
      }
      if (TL.analytics) TL.analytics.trackRaceExpand(raceId, 'upcoming');

      // Live score/odds updates for the shared runner cards while open
      var evEl = detail.querySelector('[data-event-id]');
      var ev = detail.dataset.eventId || (evEl && evEl.dataset.eventId);
      if (ev && TL.polling && !TL.state.activePollers[raceId]) {
        TL.polling.start(raceId, ev);
        TL.state.upcomingPollId = raceId;
      }

      _showOverlay();

      var firstRunner = detail.querySelector('.runner');
      if (firstRunner && !detail.querySelector('.runner.sel')) {
        TL.runner.selectRunner(firstRunner, raceId);
      }
    } else {
      TL.state.activeRaceId = null;
      _clearHash();
    }
  };

  TL.views.switchDay = function (idx) {
    document.querySelectorAll('.day-tab').forEach(function (t, i) {
      t.classList.toggle('active', i === idx);
    });
    document.querySelectorAll('.day-panel').forEach(function (p, i) {
      p.classList.toggle('active', i === idx);
    });
  };

  // ── DOM mutation helpers for schedule.js ──

  /**
   * Insert a live banner into the live section.
   * bannerHtml should be the outer HTML of the banner + its inline detail.
   */
  TL.views.insertLiveBanner = function (html) {
    var section = document.getElementById('v2LiveSection');
    if (!section) return;
    // Hide placeholder
    var ph = document.getElementById('v2LivePlaceholder');
    if (ph) ph.classList.add('hidden');
    var slot = document.getElementById('detailToggleSlot');
    if (slot) slot.classList.remove('hidden');
    var temp = document.createElement('div');
    temp.innerHTML = html;
    var children = temp.children;
    while (children.length) {
      var el = children[0];
      el.classList.add('entering');
      section.appendChild(el);
      // Trigger reflow then remove class for animation
      el.offsetHeight; // eslint-disable-line no-unused-expressions
      el.classList.remove('entering');
    }
  };

  /**
   * Remove a live banner and its inline detail from the DOM with animation.
   */
  TL.views.removeLiveBanner = function (raceId) {
    var banner = document.querySelector('.live-banner[data-race-id="' + raceId + '"]');
    var detail = document.getElementById('v2InlineDetail-' + raceId);
    // Hide overlay if this banner was expanded
    if (banner && banner.classList.contains('expanded')) {
      _hideOverlay();
    }
    function removeEl(el) {
      if (!el) return;
      el.classList.add('leaving');
      el.addEventListener('transitionend', function () { el.remove(); }, { once: true });
      // Fallback in case transitionend doesn't fire
      setTimeout(function () { if (el.parentNode) el.remove(); }, 600);
    }
    removeEl(banner);
    removeEl(detail);
    // Show placeholder if no banners remain (check after removal timeout)
    setTimeout(function () {
      if (!document.querySelector('.live-banner')) {
        var ph = document.getElementById('v2LivePlaceholder');
        if (ph) ph.classList.remove('hidden');
        var slot = document.getElementById('detailToggleSlot');
        if (slot) slot.classList.add('hidden');
      }
    }, 650);
  };

  /**
   * Insert an up-next race row (HTML fragment) sorted by race start time.
   * Returns the first inserted element (the row).
   */
  TL.views.insertRaceRow = function (html, raceStartIso) {
    var container = document.getElementById('v2UpNextRaces');
    if (!container) return;
    var temp = document.createElement('div');
    temp.innerHTML = html;

    // The fragment may be wrapped in .race-card-fragment — unwrap if so
    var fragment = temp.querySelector('.race-card-fragment');
    var els = fragment ? fragment.children : temp.children;

    // Find insertion point (sorted by race_start_iso)
    var newStart = raceStartIso ? new Date(raceStartIso).getTime() : Infinity;
    var rows = container.querySelectorAll('.race-row');
    var insertBefore = null;
    for (var i = 0; i < rows.length; i++) {
      var rowStart = rows[i].dataset.raceStart ? new Date(rows[i].dataset.raceStart).getTime() : 0;
      if (rowStart > newStart) {
        insertBefore = rows[i];
        break;
      }
    }

    // Move all children from temp into container
    var first = els[0] || null;
    while (els.length) {
      var el = els[0];
      el.classList.add('entering');
      if (insertBefore) {
        container.insertBefore(el, insertBefore);
      } else {
        container.appendChild(el);
      }
      el.offsetHeight; // eslint-disable-line no-unused-expressions
      el.classList.remove('entering');
    }

    // Hide empty state if visible
    TL.views.hideEmptyState();
    return first;
  };

  /**
   * Remove an up-next race row and its inline detail.
   */
  TL.views.removeRaceRow = function (raceId) {
    var row = document.querySelector('.race-row[data-race-id="' + raceId + '"]');
    var detail = document.getElementById('v2InlineDetail-' + raceId);
    function removeEl(el) {
      if (!el) return;
      el.classList.add('leaving');
      el.addEventListener('transitionend', function () { el.remove(); }, { once: true });
      setTimeout(function () { if (el.parentNode) el.remove(); }, 600);
    }
    removeEl(row);
    removeEl(detail);
  };

  /**
   * Transition a live banner to "Awaiting Results" state.
   *
   * Idempotent — schedule.js polls every 30s and re-invokes this for every
   * already-awaiting race in the live response, so the body must be safe to
   * call repeatedly. We use TL.track.transitionToAwaiting() rather than
   * TL.track.stop() here: stop() destroys the horse Lottie and clears
   * #v2Horse innerHTML, which on every subsequent poll would wipe the
   * jockey-silk horse the user expects to see parked at the finish line.
   */
  TL.views.showAwaitingState = function (raceId) {
    var banner = document.querySelector('.live-banner[data-race-id="' + raceId + '"]');
    if (!banner) return;
    var alreadyAwaiting = banner.classList.contains('awaiting');
    banner.classList.add('awaiting');
    var chip = banner.querySelector('.lb-live-chip');
    if (chip) {
      chip.innerHTML = '<span class="dot"></span> ';
      var chipText = document.createElement('span');
      chipText.textContent = 'Waiting for the result';
      if (TL.gloss && TL.gloss.tag) TL.gloss.tag(chipText, 'awaiting_result');
      chip.appendChild(chipText);
    }
    // Soft-halt the track animation if this banner is expanded — keep the
    // horse Lottie visible at the finish line, just slow it to idle. Skip
    // re-applying once the banner is already in awaiting state to avoid
    // resetting the idle state on every poll.
    if (!alreadyAwaiting && banner.classList.contains('expanded') && TL.track) {
      if (typeof TL.track.transitionToAwaiting === 'function') {
        TL.track.transitionToAwaiting();
      } else {
        TL.track.stop();
      }
    }
  };

  /**
   * Races later today beyond the up-next window (server-rendered
   * data-later-count on #v2UpNextRaces).
   */
  function _laterCount() {
    var list = document.getElementById('v2UpNextRaces');
    var n = list ? parseInt(list.getAttribute('data-later-count'), 10) : 0;
    return n > 0 ? n : 0;
  }

  /**
   * Refresh the later-today count and copy from the schedule poll.
   * nextLater is {time, venue_name} or null. Leaves the "See all" link alone.
   */
  TL.views.setLaterState = function (count, nextLater) {
    var n = count > 0 ? count : 0;
    var list = document.getElementById('v2UpNextRaces');
    if (list) list.setAttribute('data-later-count', String(n));
    var el = document.getElementById('v2LaterState');
    if (!el) return;
    var nextEl = el.querySelector('[data-slot="next-later"]');
    if (nextEl) {
      nextEl.textContent = nextLater && nextLater.time
        ? 'Next race ' + nextLater.time + ' at ' + (nextLater.venue_name || '')
        : '';
    }
    var countEl = el.querySelector('[data-slot="later-count"]');
    if (countEl) {
      // "Next race ..." names one of the later races, so "more" leaves it out
      var hasNext = !!(nextLater && nextLater.time);
      var more = hasNext ? n - 1 : n;
      if (hasNext && more < 1) {
        countEl.textContent = '';
      } else {
        countEl.textContent = more + (hasNext ? ' more' : '') + ' race' + (more !== 1 ? 's' : '') + ' later today';
      }
    }
  };

  /**
   * Keep the Live tab's "Nothing live right now" line current (team#170).
   * nextRace is {venue_name, time} (time as on the race rows) or null.
   */
  TL.views.setNextRace = function (nextRace) {
    var ph = document.getElementById('v2LivePlaceholder');
    var line = ph && ph.querySelector('[data-slot="next-race"]');
    if (!line) return;
    line.textContent = nextRace && nextRace.time
      ? 'Nothing live right now. Next race: ' + nextRace.venue_name + ' ' + nextRace.time + ' ' + (ph.dataset.day || 'today') + '.'
      : 'Nothing live right now. We\'ll show races here as soon as they\'re on.';
  };

  /**
   * Show empty state message (or the later-today state when races remain).
   */
  TL.views.showEmptyState = function () {
    // Only claim "No more races today" when nothing is left later today.
    var later = _laterCount() > 0;
    var empty = document.getElementById('v2EmptyState');
    var laterEl = document.getElementById('v2LaterState');
    if (empty) empty.classList.toggle('hidden', later);
    if (laterEl) laterEl.classList.toggle('hidden', !later);
  };

  /**
   * Hide empty state message (and the later-today state).
   */
  TL.views.hideEmptyState = function () {
    ['v2EmptyState', 'v2LaterState'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.classList.add('hidden');
    });
  };

  /**
   * Update the "X races coming up" subtitle.
   */
  TL.views.updateUpNextCount = function () {
    var subtitle = document.getElementById('v2UpNextSubtitle');
    if (!subtitle) return;
    var rows = document.querySelectorAll('#v2UpNextRaces .race-row');
    var count = rows.length;
    var later = _laterCount();
    if (count === 0 && later > 0) {
      subtitle.textContent = later + ' race' + (later !== 1 ? 's' : '') + ' later today';
      return;
    }
    subtitle.textContent = count + ' race' + (count !== 1 ? 's' : '') + ' coming up';
  };

  /**
   * Mark a race row as "Live" in-place (upcoming page only).
   * Adds a pulsing badge and a link to the live tab — does NOT remove the row.
   */
  TL.views.markRaceRowLive = function (raceId) {
    var row = document.querySelector('.race-row[data-race-id="' + raceId + '"]');
    if (!row || row.classList.contains('race-row-live')) return; // already badged
    row.classList.add('race-row-live');

    // Find the status area and replace its content with a live chip
    var status = row.querySelector('.rr-status');
    if (status) {
      // A link row (Upcoming, team#179) can't hold a nested link: the whole row points at the live tab instead
      var isLink = row.tagName === 'A';
      if (isLink) row.setAttribute('href', '/races/');
      status.innerHTML = '<div class="rs-live-badge"><span class="dot"></span> Live now</div>'
        + (isLink
          ? '<span class="rs-live-link">Watch live &rarr;</span>'
          : '<a class="rs-live-link" href="/races/" onclick="event.stopPropagation()">Watch live &rarr;</a>');
    }
  };

  /**
   * Mark a race row as "Finished" in-place (upcoming page only).
   */
  TL.views.markRaceRowFinished = function (raceId) {
    var row = document.querySelector('.race-row[data-race-id="' + raceId + '"]');
    if (!row || row.classList.contains('race-row-finished')) return;
    row.classList.remove('race-row-live');
    row.classList.add('race-row-finished');

    var status = row.querySelector('.rr-status');
    if (status) {
      status.innerHTML = '<div class="rs-finished-badge"></div>';
      var badgeText = document.createElement('span');
      badgeText.textContent = 'Finished';
      if (TL.gloss && TL.gloss.tag) TL.gloss.tag(badgeText, 'finished');
      status.firstChild.appendChild(badgeText);
    }
  };

  /**
   * Build a live banner from the template and schedule data, for dynamic promotion.
   * Returns an HTML string (banner + inline detail placeholder).
   */
  TL.views.buildLiveBannerFromData = function (raceData) {
    var tpl = document.getElementById('v2LiveBannerTpl');
    if (!tpl) return '';

    var frag = tpl.content.cloneNode(true);
    var banner = frag.querySelector('.live-banner');
    if (!banner) return '';

    banner.dataset.raceId = raceData.id;
    banner.dataset.eventId = raceData.event_id;
    banner.dataset.raceStart = raceData.race_start_iso || '';
    banner.dataset.offTime = raceData.off_dt_iso || '';
    banner.dataset.duration = raceData.race_duration_seconds || 0;
    banner.setAttribute('onclick', "v2ToggleLiveBanner('" + raceData.id + "')");

    var titleEl = frag.querySelector('[data-slot="title"]');
    if (titleEl) titleEl.textContent = raceData.race_name || '';

    var tvEl = frag.querySelector('[data-slot="time-venue"]');
    if (tvEl) tvEl.textContent = (raceData.scheduled_time || '') + ' · ' + (raceData.venue_name || '');

    var detailsEl = frag.querySelector('[data-slot="details"]');
    if (detailsEl) {
      function addPill(text, bold) {
        var span = document.createElement('span');
        span.className = 'lb-pill';
        if (bold) {
          var strong = document.createElement('strong');
          strong.textContent = text;
          span.appendChild(strong);
        } else {
          span.textContent = text;
        }
        detailsEl.appendChild(span);
      }
      if (raceData.race_type) addPill(raceData.race_type, true);
      if (raceData.distance_plain) addPill(raceData.distance_plain, true);
      if (raceData.going_plain) addPill(raceData.going_plain, false);
      if (raceData.number_of_runners) addPill(raceData.number_of_runners + (raceData.number_of_runners === 1 ? ' runner' : ' runners'), false);
    }

    var cdEl = frag.querySelector('.cd-time');
    if (cdEl) {
      cdEl.dataset.raceStart = raceData.race_start_iso || '';
      cdEl.dataset.duration = raceData.race_duration_seconds || 0;
    }

    // Serialize to HTML string
    var div = document.createElement('div');
    div.appendChild(frag);
    var html = div.innerHTML;
    return html;
  };
})();
